import { observable, computed, action } from "mobx";
import Autobind from "autobind-decorator";
import RootStore from "./rootStore";
import SeriesMetadataModel from "models/seriesMetadataModel";
import MetadataCache from "core/cache/Metadata";
import Helper from "helper";

/**
 * @class WorklistStore
 * @description 요청에 따른 Study 목록을 반환
 */
@Autobind
class MetadataStore {
  _rootStore: RootStore; /// rootStore 객체. 다른 Store를 연계할 때 사용

  @observable
  _seriesMetadataModelMap: Map<string, SeriesMetadataModel> = new Map();

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;
  }

  getGroupByStudyDate() {
    // Series 갯수 획득
    const numOfSeries = this._seriesMetadataModelMap.size;
    if (numOfSeries === 0) {
      return {};
    }

    // study date 를 기준으로 데이터를 그룹화 한다.
    let seriesGroups = {} as any;
    this._seriesMetadataModelMap.forEach((seriesMetadata: any, key: any) => {
      const studyDate = seriesMetadata.studyDate;
      if (!seriesGroups.hasOwnProperty(studyDate)) {
        seriesGroups[studyDate] = [seriesMetadata];
      } else {
        seriesGroups[studyDate].push(seriesMetadata);
      }
    });

    // 각 날짜 별로 series Number에 따라 재 정렬 한다.
    this._seriesMetadataModelMap.forEach((seriesMetadata: any, key: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      (seriesMetadata: any) => {
        const studyDate = seriesMetadata.studyDate;
        if (seriesGroups.hasOwnProperty(studyDate)) {
          seriesGroups[studyDate] = seriesGroups[studyDate].sort(
            (a: any, b: any) =>
              /// parseInt에 입력 파라미터로 기대하지 않은 타입 (null, string 등)이
              /// 입력 된다고 하더라도 Exception을 발생시키지 않고 NaN을 반환
              /// sort 는 비교 구문 실행중 비교 값에 NaN이 있다고 하더라도 빈배열을 반환하거나
              /// Exception, error를 발생하지 않음
              /// 아래에서 라인에서 발생할 수 있는 문제는 a 와 b 가 seriesNumber Attribute를 가지지
              /// 않을때인데, 사실 해당 Attribute를 가지지 않는다고해서 에러를 발생시키지 않는다.
              parseInt(a.seriesNumber) > parseInt(b.seriesNumber) ? 1 : -1
          );
        }
      };
    });
    return seriesGroups;
  }
  getModailty(metadataUID: string, indexOnStack: number) {
    let metadataCache = new MetadataCache();
    const modality = metadataCache.getFrameModality(metadataUID, indexOnStack);

    return modality;
  }
  dump(metadataUID: string, indexOnStack: number) {
    let metadataCache = new MetadataCache();
    const image = metadataCache.getFrame(metadataUID, indexOnStack);

    return image.dump;
  }

  getNumberOfFrames(metadataUID: string) {
    if (this._seriesMetadataModelMap.has(metadataUID) === false) {
      return 0;
    }
    return (this._seriesMetadataModelMap.get(metadataUID) as any)
      .numberOfFrames;
  }

  /**
   * @description 새 Metadata를 store에 저장
   * @function add
   * @param  {string} uniqueID    Metadata의 Unique id
   * @param  {any} seriesMetadata Metadata 정보
   */
  @action
  add(uniqueID: string, seriesMetadata: any) {
    Helper.Log.Dev(`@action : metadataStore::add. seriesID : ${uniqueID}`);

    if (this._seriesMetadataModelMap.has(uniqueID) === true) {
      (this._seriesMetadataModelMap.get(
        uniqueID
      ) as SeriesMetadataModel).setSeriesMetadata(
        seriesMetadata.modality,
        seriesMetadata.numOfFrame,
        seriesMetadata.seriesID,
        seriesMetadata.seriesDescription,
        seriesMetadata.seriesInstanceUID,
        seriesMetadata.seriesNumber,
        seriesMetadata.studyDate,
        seriesMetadata.studyDescription,
        seriesMetadata.studyTime
      );
    } else {
      let metadata = new SeriesMetadataModel();
      metadata.setSeriesMetadata(
        seriesMetadata.modality,
        seriesMetadata.numOfFrame,
        seriesMetadata.seriesID,
        seriesMetadata.seriesDescription,
        seriesMetadata.seriesInstanceUID,
        seriesMetadata.seriesNumber,
        seriesMetadata.studyDate,
        seriesMetadata.studyDescription,
        seriesMetadata.studyTime
      );
      this._seriesMetadataModelMap.set(uniqueID, metadata);
      Helper.Log.Dev(
        `@action : metadataStore::add. uniqueID : ${uniqueID} leastItems : ${this._seriesMetadataModelMap.size}`
      );

      // MetadatStore에 데이터 입력시, SeriesList에도 데이터 목록 동기화 및
      // Thumbnail slice 지정
      this._rootStore.seriesListStore.addSeriesItem(uniqueID);
    }
  }

  /**
   * @description _seriesMetadataModelMap에서 특정 uniqueID를 가진 Metadata를 삭제한다.
   * @function delete
   * @param  {string} uniqueID  메타데이터를 표현하는 uniqueID
   */
  @action
  delete(uniqueID: string) {
    this._rootStore.gridLayoutStore.remove(uniqueID);
    let isFound = false;
    /// 1. Remove metadata in store
    this._seriesMetadataModelMap.forEach((seriesMetadata: any, key: any) => {
      if (key === uniqueID) {
        isFound = true;
      }
    });
    if (isFound === false) {
      return;
    }

    this._seriesMetadataModelMap.delete(uniqueID);

    /// 2. Remove metadata in store
    let metadataCache = new MetadataCache();
    metadataCache.del(uniqueID);
    Helper.Log.Dev(
      `@action : metadataStore::delete. uniqueID : ${uniqueID} leastItems : ${this._seriesMetadataModelMap.size}`
    );
    this._rootStore.seriesListStore.delSeriesItem(uniqueID);
  }

  /**
   * @description _seriesMetadataMap 에 저장된 모든 Metadata 정보 및 Caching 정보를 삭제한다.
   * @function clear
   */
  @action
  clear() {
    this._rootStore.gridLayoutStore.clearAll();
    /// 1. Remove all cache in store
    this._seriesMetadataModelMap.forEach((seriesMetadata: any, key: any) => {
      this._seriesMetadataModelMap.delete(key);
      this._rootStore.seriesListStore.delSeriesItem(key);
    });

    /// 2. Remove all cache in provider
    let metadataCache = new MetadataCache();
    metadataCache.clear();
    Helper.Log.Dev(
      `@action : metadataStore::clear. leastItems : ${this._seriesMetadataModelMap.size}`
    );
  }
}

export default MetadataStore;
